import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useAdminAuth } from "@/context/AdminAuth";
import { apiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-16" data-testid="admin-login-page">
      <div className="glow-orb left-1/2 top-1/3 h-[420px] w-[520px] -translate-x-1/2 bg-[#4C5A8C]/25" aria-hidden />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0D1322] p-6 sm:p-10"
      >
        <div className="flex items-center gap-3">
          <img src="/assets/lanvaus-mark.png" alt="Lanvaus logo" width="323" height="260" className="h-8 w-auto" />
          <img src="/assets/lanvaus-wordmark.png" alt="Lanvaus" width="778" height="120" className="h-[20px] w-auto" />
        </div>
        <h1 className="mt-8 font-display text-2xl font-bold text-white">Admin console</h1>
        <p className="mt-2 text-sm text-[#A6B0C9]">
          Manage downloadable resources and view inbound leads.
        </p>

        <label className="mt-8 block">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-lg border border-white/12 bg-[#0B0F1A] px-4 py-3 text-sm text-white outline-none transition-colors duration-200 focus:border-[#8E9AEA]/60"
            data-testid="admin-email-input"
          />
        </label>
        <label className="mt-5 block">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]">Password</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full rounded-lg border border-white/12 bg-[#0B0F1A] px-4 py-3 text-sm text-white outline-none transition-colors duration-200 focus:border-[#8E9AEA]/60"
            data-testid="admin-password-input"
          />
        </label>

        {error && (
          <p className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={busy} className="btn-primary mt-7 w-full disabled:opacity-60" data-testid="admin-login-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs text-[#A6B0C9] transition-colors duration-200 hover:text-white" data-testid="admin-back-home">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to lanvaus.com
        </Link>
      </motion.form>
    </div>
  );
}
